import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  aggregateBenchmarkByInstitution,
  BENCHMARK_CORE_STATS,
  BENCHMARK_INSTITUTION_COLORS,
  formatBenchmarkStatValue,
  getBenchmarkInstitutionAbbreviation,
} from "../../benchmarkMetricConfig";
import styles from "./StackedBarChart.module.scss";

const TOOLTIP_EST_W = 260;
const TOOLTIP_EST_H = 120;
const TOOLTIP_EDGE = 8;
const TOOLTIP_GAP = 12;

const getTooltipFixedStyle = (clientX, clientY) => {
  if (typeof window === "undefined") {
    return { position: "fixed", left: 0, top: 0, zIndex: 1600 };
  }
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let left = clientX + TOOLTIP_GAP;
  let top = clientY - TOOLTIP_EST_H - TOOLTIP_GAP;
  if (left + TOOLTIP_EST_W > vw - TOOLTIP_EDGE) {
    left = clientX - TOOLTIP_EST_W - TOOLTIP_GAP;
  }
  left = Math.max(TOOLTIP_EDGE, Math.min(left, vw - TOOLTIP_EST_W - TOOLTIP_EDGE));
  if (top < TOOLTIP_EDGE) {
    top = clientY + TOOLTIP_GAP;
  }
  if (top + TOOLTIP_EST_H > vh - TOOLTIP_EDGE) {
    top = vh - TOOLTIP_EST_H - TOOLTIP_EDGE;
  }
  return { position: "fixed", left, top, zIndex: 1600 };
};

const InstitutionLogo = ({ photoUrl, fallbackText }) => {
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setFailed(false);
  }, [photoUrl]);
  if (photoUrl && !failed) {
    return (
      <img
        src={photoUrl}
        alt=""
        className={styles["tooltip-logo"]}
        loading="lazy"
        onError={() => setFailed(true)}
      />
    );
  }
  return (
    <div className={styles["tooltip-logo-fallback"]}>
      {fallbackText}
    </div>
  );
};

const BarTooltip = ({ inst, stat, value, clientX, clientY }) => {
  const style = useMemo(
    () => getTooltipFixedStyle(clientX, clientY),
    [clientX, clientY]
  );

  if (!inst || typeof document === "undefined" || !document.body) return null;

  return createPortal(
    <div className={styles["bar-tooltip"]} style={style}>
      <div className={styles["bar-tooltip__header"]}>
        <InstitutionLogo
          photoUrl={inst.photo_url}
          fallbackText={getBenchmarkInstitutionAbbreviation(inst.name)}
        />
        <span className={styles["bar-tooltip__name"]}>{inst.name}</span>
      </div>
      <div className={styles["bar-tooltip__stat"]}>
        <span className={styles["bar-tooltip__stat-label"]}>{stat.label}</span>
        <span className={styles["bar-tooltip__stat-value"]}>
          {formatBenchmarkStatValue(value, stat)}
        </span>
      </div>
    </div>,
    document.body
  );
};

const OverlappingChart = ({ stat, institutions, colorMap }) => {
  const containerRef = useRef(null);
  const [hovered, setHovered] = useState(null);
  // Width proportions use magnitude so signed metrics (e.g. CAGR) still render
  // a visible bar; the annotation/tooltip keep the true signed value.
  const totalMagnitude = institutions.reduce(
    (acc, inst) => acc + Math.abs(inst[stat.key] || 0),
    0
  );

  const handleMouseEnter = useCallback(
    (inst, e) => {
      setHovered({
        inst,
        value: inst[stat.key] || 0,
        clientX: e.clientX,
        clientY: e.clientY,
      });
    },
    [stat.key]
  );

  const handleMouseMove = useCallback((e) => {
    setHovered((prev) =>
      prev
        ? { ...prev, clientX: e.clientX, clientY: e.clientY }
        : null
    );
  }, []);

  const handleMouseLeave = useCallback(() => {
    setHovered(null);
  }, []);

  // Annotation shows the leading value (by magnitude) for this metric.
  const topValue = institutions.reduce((best, inst) => {
    const v = inst[stat.key] || 0;
    return Math.abs(v) > Math.abs(best) ? v : best;
  }, 0);

  return (
    <div className={styles["overlap-chart"]} ref={containerRef}>
      <div className={styles["overlap-chart__head"]}>
        <div>
          <span className={styles["overlap-chart__category"]}>{stat.label}</span>
        </div>
        <span className={styles["overlap-chart__annotation"]}>
          {formatBenchmarkStatValue(topValue, stat)}
        </span>
      </div>
      <div className={styles["overlap-chart__bars"]}>
        {institutions.map((inst) => {
          const value = inst[stat.key] || 0;
          const pct =
            totalMagnitude > 0 ? (Math.abs(value) / totalMagnitude) * 100 : 0;
          const color = colorMap[inst.institution_id] || BENCHMARK_INSTITUTION_COLORS[0];

          return (
            <div
              key={inst.institution_id}
              className={styles["bar-layer"]}
              style={{ width: `${Math.max(pct, 0)}%` }}
            >
              <div
                className={styles["bar"]}
                style={{
                  backgroundColor: color,
                }}
                onMouseEnter={(e) => handleMouseEnter(inst, e)}
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
              />
            </div>
          );
        })}
      </div>
      {hovered && (
        <BarTooltip
          inst={hovered.inst}
          stat={stat}
          value={hovered.value}
          clientX={hovered.clientX}
          clientY={hovered.clientY}
        />
      )}
    </div>
  );
};

const StackedBarChart = ({
  rawData,
  institutionIds,
  showHeader = true,
  stats = BENCHMARK_CORE_STATS,
  colorMap: colorMapProp,
}) => {
  const chartData = useMemo(
    () => aggregateBenchmarkByInstitution(rawData, institutionIds),
    [rawData, institutionIds]
  );
  const orderedChartData = useMemo(() => {
    const byId = new Map(chartData.map((inst) => [inst.institution_id, inst]));
    const ordered = institutionIds.map((id) => byId.get(id)).filter(Boolean);
    const remaining = chartData.filter((inst) => !institutionIds.includes(inst.institution_id));
    return [...ordered, ...remaining];
  }, [chartData, institutionIds]);

  const colorMap = useMemo(() => {
    if (colorMapProp) return colorMapProp;
    const map = {};
    institutionIds.forEach((id, idx) => {
      map[id] = BENCHMARK_INSTITUTION_COLORS[idx % BENCHMARK_INSTITUTION_COLORS.length];
    });
    return map;
  }, [institutionIds, colorMapProp]);

  // A metric is shown when at least one entity has a finite, non-zero value —
  // `!== 0` (rather than `> 0`) keeps signed metrics like CAGR visible.
  const visibleStats = useMemo(
    () =>
      stats.filter((stat) =>
        orderedChartData.some((inst) => {
          const v = Number(inst[stat.key]);
          return Number.isFinite(v) && v !== 0;
        })
      ),
    [stats, orderedChartData]
  );

  if (!orderedChartData.length) return null;

  const rootClass = [
    styles["stacked-chart"],
    !showHeader && styles["stacked-chart--embed"],
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={rootClass} id="stacked-bar-chart-section">
      {showHeader ? (
        <div className={styles["stacked-chart__header"]}>
          <div>
            <h3 className={styles["stacked-chart__title"]}>
              Inter-Institutional Metric Scaling
            </h3>
          </div>

          <div className={styles["legend"]}>
            {orderedChartData.map((inst) => (
              <div key={inst.institution_id} className={styles["legend-item"]}>
                <div
                  className={styles["legend-swatch"]}
                  style={{
                    backgroundColor: colorMap[inst.institution_id],
                  }}
                />
                <span className={styles["legend-name"]}>
                  {getBenchmarkInstitutionAbbreviation(inst.name)}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div className={styles["charts-grid"]}>
        {visibleStats.map((stat) => (
          <OverlappingChart
            key={stat.key}
            stat={stat}
            institutions={orderedChartData}
            colorMap={colorMap}
          />
        ))}
      </div>
    </div>
  );
};

export default StackedBarChart;
